/**
 * UtilityCluster — right region of the WorkspaceShell top nav.
 *
 * Order (left to right on desktop):
 *   1. Notification tray (Bell)
 *   2. Blind mode toggle
 *   3. Avatar pill  [Name + avatar circle] — opens dropdown:
 *        • Theme toggle
 *        • Language selector
 *        • App Tour
 *        • Sign out
 */
import {
  Bell,
  Sun,
  Moon,
  LogOut,
  Globe,
  Compass,
  Settings,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import useMediaQuery from '../../../hooks/useMediaQuery';

import BlindModeToggle from './BlindModeToggle';
import CreditsPill from './CreditsPill';
import { useTheme } from '../../../contexts/ThemeContext';
import { SUPPORTED_LANGUAGES } from '../../../i18n';
import {
  clearAllUserNotifications,
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  NOTIFICATIONS_REFRESH_EVENT,
} from '../../../services/objectivesTasksService';
import type { UserNotification } from '../../../types/objectivesTasks';
import { getCurrentUserId } from '../../../utils/authUtils';
import type { DashboardPage } from '../../dashboard/components/Layout';
import { GGJLogoMark } from '../../../components/Brand/GGJLogo';
import { APP_VERSION } from '../../../config/appVersion';
import { FORCE_PRODUCT_TOUR_EVENT } from '../../../lib/productTourSteps';
import ProfilePlanCard from './ProfilePlanCard';


export interface UtilityClusterProps {
  /** Display name (e.g. "Alice Smith") for avatar tooltip + initials. */
  userName: string | null;
  /** Email address (shown in dropdown header). */
  userEmail: string;
  /** Optional avatar image URL (Google / OAuth). */
  userAvatarUrl?: string | null;
  /** Current credits — null while loading is true. */
  credits: number | null;
  /** True while the credits/plan is still loading. */
  creditsLoading: boolean;
  /** Open the credit purchase modal — preserved from legacy Layout. */
  onUpgradeClick: () => void;
  /** Trigger the sign-out confirmation dialog. */
  onSignOutClick: () => void;
  /** Navigation handler — used by avatar menu items. */
  onNavChange: (page: DashboardPage) => void;
  /** Show the truncated/compact mode (mobile). */
  isDesktop: boolean;
}


/** First+last initial of a person's name. */
function nameInitials(name: string | null | undefined): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}


// ─────────────────────────────────────────────────────────────────────────────
// Internal: notification tray
// ─────────────────────────────────────────────────────────────────────────────
function NotificationTrayButton({ onNavChange }: { onNavChange: (page: DashboardPage) => void }) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const refreshUnreadCount = useCallback(() => {
    getCurrentUserId().then((uid) => {
      if (!uid) return;
      getNotifications(uid, { limit: 50 }).then((list) => {
        setUnreadCount(list.filter((n) => !n.is_read).length);
      });
    });
  }, []);

  useEffect(() => {
    refreshUnreadCount();
    const poll = window.setInterval(() => refreshUnreadCount(), 90_000);
    return () => window.clearInterval(poll);
  }, [refreshUnreadCount]);

  useEffect(() => {
    const onRefresh = () => refreshUnreadCount();
    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, onRefresh);
  }, [refreshUnreadCount]);

  useEffect(() => {
    if (!open || isMobile) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [open, isMobile]);

  useEffect(() => {
    if (!open || !isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open, isMobile]);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setLoading(true);
    getCurrentUserId()
      .then((uid) => (uid ? getNotifications(uid, { limit: 20 }) : []))
      .then((list) => {
        if (!mounted) return;
        setNotifications(list);
        setUnreadCount(list.filter((n) => !n.is_read).length);
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onRefresh = () => {
      getCurrentUserId()
        .then((uid) => (uid ? getNotifications(uid, { limit: 20 }) : []))
        .then((list) => {
          setNotifications(list);
          setUnreadCount(list.filter((n) => !n.is_read).length);
        });
    };
    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, onRefresh);
  }, [open]);

  const handleMarkRead = (uid: string | null, id: string) => {
    if (!uid) return;
    markNotificationRead(uid, id).then(() => {
      setNotifications((prev) => {
        const updated = prev.map((n) => (n.id === id ? { ...n, is_read: true } : n));
        setUnreadCount(updated.filter((n) => !n.is_read).length);
        return updated;
      });
    });
  };

  const handleMarkAllRead = () => {
    getCurrentUserId().then((uid) => {
      if (!uid) return;
      markAllNotificationsRead(uid).then(() => {
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
        setUnreadCount(0);
      });
    });
  };

  const handleClearAll = () => {
    if (clearing || notifications.length === 0) return;
    getCurrentUserId().then((uid) => {
      if (!uid) return;
      setClearing(true);
      clearAllUserNotifications(uid)
        .then(() => {
          setNotifications([]);
          setUnreadCount(0);
        })
        .catch((err) => console.warn('[NotificationTray] clear all failed:', err))
        .finally(() => setClearing(false));
    });
  };

  const hasTrayItems = notifications.length > 0;

  const handleNotificationClick = (n: UserNotification) => {
    const isBlog =
      n.type === 'blog_draft_created' ||
      n.type === 'blog_draft_stale_reminder' ||
      n.type === 'blog_auto_published';
    const isDailyBrief = n.type === 'daily_brief';

    if (!isBlog && !isDailyBrief) return;

    void getCurrentUserId().then((uid) => {
      if (uid && !n.is_read) void markNotificationRead(uid, n.id);
    });

    if (isBlog) {
      window.history.pushState({}, '', '/admin/blogs');
      window.dispatchEvent(new PopStateEvent('popstate'));
      onNavChange('blog-cms');
    } else {
      window.history.pushState({}, '', '/jobs');
      window.dispatchEvent(new PopStateEvent('popstate'));
      onNavChange('jobs');
    }
    setOpen(false);
  };

  const trayBody = (
    <>
      <div
        className="ds-notification-tray__header p-3 border-b flex items-center justify-between gap-2 shrink-0"
        style={{ background: 'var(--ds-brand)', borderColor: 'var(--ds-border)' }}
      >
        <p className="t-card-title text-white">{t('layout.notifications', 'Notifications')}</p>
        <div className="flex items-center gap-1 shrink-0">
          {(unreadCount > 0 || hasTrayItems) && (
            <div className="ds-notification-tray__header-actions flex items-center gap-1 shrink-0">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  disabled={clearing}
                  className="ds-notification-tray__header-btn px-2 t-tag text-white/80 hover:text-white hover:underline touch-manipulation disabled:opacity-50"
                >
                  {t('layout.markAllRead', 'Mark all read')}
                </button>
              )}
              {hasTrayItems && (
                <button
                  type="button"
                  onClick={handleClearAll}
                  disabled={clearing}
                  className="ds-notification-tray__header-btn px-2 t-tag text-white/80 hover:text-white hover:underline touch-manipulation disabled:opacity-50"
                  aria-label={t('layout.clearAllNotifications', 'Clear all notifications')}
                >
                  {clearing
                    ? t('layout.loading', 'Loading…')
                    : t('layout.clearAllNotifications', 'Clear all')}
                </button>
              )}
            </div>
          )}
          {isMobile && (
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="ds-notification-tray__close min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-white/90 hover:text-white hover:bg-white/10 touch-manipulation"
              aria-label={t('layout.close', 'Close')}
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          )}
        </div>
      </div>
      <div className="ds-notification-tray__body overflow-y-auto flex-1 min-h-0 py-1">
        {loading ? (
          <p className="t-body t-muted px-4 py-3">{t('layout.loading', 'Loading…')}</p>
        ) : notifications.length === 0 ? (
          <p className="t-body t-muted px-4 py-3">{t('layout.noNotifications', 'No new notifications')}</p>
        ) : (
          <ul>
            {notifications.map((n) => {
              const isClickable = n.type === 'blog_draft_created'
                || n.type === 'blog_draft_stale_reminder'
                || n.type === 'blog_auto_published'
                || n.type === 'daily_brief';
              return (
                <li
                  key={n.id}
                  className={`ds-notification-tray__item px-3 py-2.5 border-b last:border-b-0 ${!n.is_read ? 'bg-[var(--ds-brand-soft)]' : ''} ${isClickable ? 'cursor-pointer hover:bg-[var(--ds-surface-hover)]' : ''}`}
                  style={{ borderColor: 'var(--ds-border)' }}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  onClick={() => handleNotificationClick(n)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleNotificationClick(n);
                    }
                  }}
                >
                  <p className="ds-notification-tray__message">{n.message}</p>
                  <p className="ds-notification-tray__meta mt-0.5">
                    {new Date(n.created_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
                  </p>
                  {!n.is_read && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        getCurrentUserId().then((uid) => handleMarkRead(uid, n.id));
                      }}
                      className="ds-notification-tray__meta t-brand hover:underline mt-1 min-h-[44px] inline-flex items-center touch-manipulation"
                    >
                      {t('layout.markRead', 'Mark read')}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="min-h-[44px] min-w-[44px] h-11 w-11 ds-workspace-icon-button relative touch-manipulation"
        aria-label={t('layout.notifications', 'Notifications')}
        aria-expanded={open}
      >
        <Bell className="w-5 h-5" aria-hidden />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[1.25rem] h-5 px-1 flex items-center justify-center rounded-full t-tag bg-[var(--ds-danger)] text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {open && !isMobile && (
        <div className="ds-notification-tray absolute right-0 mt-2 w-80 max-h-[70vh] flex flex-col ds-card-pop !p-0 z-[1010] overflow-hidden">
          {trayBody}
        </div>
      )}
      {open && isMobile && createPortal(
        <>
          <button
            type="button"
            className="ds-notification-tray__backdrop fixed inset-0 z-[1015] cursor-pointer border-0 bg-black/50 p-0 touch-manipulation"
            aria-label={t('layout.close', 'Close')}
            onClick={() => setOpen(false)}
          />
          <div
            className="ds-notification-tray ds-notification-tray--mobile-sheet fixed inset-x-0 bottom-0 z-[1020] flex max-h-[min(85dvh,100%)] flex-col ds-card-pop !p-0 overflow-hidden"
            role="dialog"
            aria-label={t('layout.notifications', 'Notifications')}
          >
            {trayBody}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Internal: avatar pill + dropdown (name in top bar, settings inside)
// ─────────────────────────────────────────────────────────────────────────────
interface AvatarMenuProps extends Pick<UtilityClusterProps,
  'userName' | 'userEmail' | 'userAvatarUrl' | 'onSignOutClick' | 'onNavChange'
  | 'credits' | 'creditsLoading' | 'onUpgradeClick'
> {}

function AvatarMenu({
  userName,
  userEmail,
  userAvatarUrl,
  onSignOutClick,
  onNavChange,
  credits,
  creditsLoading,
  onUpgradeClick,
}: AvatarMenuProps) {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const [langOpen, setLangOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  const firstName = userName?.split(' ')[0] ?? userEmail?.split('@')[0] ?? 'Account';

  const updatePosition = () => {
    const el = btnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
  };

  useEffect(() => {
    if (!open) { setPos(null); setLangOpen(false); return; }
    updatePosition();
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-avatar-menu]') || target.closest('[data-avatar-button]')) return;
      setOpen(false);
    };
    document.addEventListener('click', onClickOutside);
    return () => document.removeEventListener('click', onClickOutside);
  }, [open]);

  const close = () => setOpen(false);

  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === i18n.language)?.label ?? 'English';

  return (
    <>
      {/* Name + avatar pill */}
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        data-avatar-button
        className="flex items-center gap-1.5 h-7 pl-2 pr-1 rounded-md border border-slate-100 bg-slate-50 ds-workspace-icon-button touch-manipulation shrink-0 dark:border-slate-700 dark:bg-slate-800/60"
        aria-label={t('layout.profile.profile', 'Account')}
        aria-expanded={open}
      >
        <span className="t-body t-strong hidden sm:block leading-none">{firstName}</span>
        <div
          className="h-6 w-6 rounded-full flex items-center justify-center shrink-0 overflow-hidden t-tag font-semibold"
          style={{ background: 'var(--ds-brand)', color: 'var(--ds-text-on-brand)' }}
        >
          {userAvatarUrl
            ? <img src={userAvatarUrl} alt="Your profile photo" className="w-full h-full object-cover" />
            : <span>{nameInitials(userName)}</span>
          }
        </div>
      </button>

      {open && pos && createPortal(
        <div
          className="fixed z-[1010] w-[min(100vw-2rem,280px)] ds-card-pop !p-0 overflow-hidden"
          style={{ top: pos.top, right: pos.right }}
          data-avatar-menu
          role="menu"
        >
          {/* Identity header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b"
            style={{ background: 'color-mix(in srgb, var(--ds-brand) 7%, transparent)', borderColor: 'var(--ds-border)' }}>
            <div className="w-8 h-8 rounded-full shrink-0 overflow-hidden flex items-center justify-center t-meta font-bold text-white"
              style={{ background: 'var(--ds-brand)' }}>
              {userAvatarUrl
                ? <img src={userAvatarUrl} alt="Your profile photo" className="w-full h-full object-cover" />
                : nameInitials(userName)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate t-body t-strong leading-tight">
                {userName || userEmail?.split('@')[0]}
              </p>
              {userEmail && (
                <p className="truncate t-meta mt-0.5">{userEmail}</p>
              )}
            </div>
          </div>

          <ProfilePlanCard
            onUpgradeClick={() => { close(); onUpgradeClick(); }}
          />

          {/* Settings */}
          <div className="py-1.5">

            {/* Theme toggle */}
            <button
              type="button"
              role="menuitem"
              onClick={() => { toggleTheme(); close(); }}
              className="ds-workspace-nav-item flex w-full items-center gap-2.5 px-4 py-2.5 text-left"
            >
              {theme === 'dark'
                ? <Sun className="h-4 w-4 shrink-0" aria-hidden />
                : <Moon className="h-4 w-4 shrink-0" aria-hidden />}
              <span className="flex-1">{theme === 'dark'
                ? t('layout.profile.lightMode', 'Light mode')
                : t('layout.profile.darkMode', 'Dark mode')}</span>
            </button>

            {/* Language — inline expand */}
            <button type="button" role="menuitem" onClick={() => setLangOpen((v) => !v)}
              className="ds-workspace-nav-item flex w-full items-center gap-2.5 px-4 py-2.5 text-left">
              <Globe className="h-4 w-4 shrink-0" aria-hidden />
              <span className="flex-1">{t('layout.language', 'Language')}</span>
              <span className="t-meta t-muted">{currentLang}</span>
            </button>
            {langOpen && (
              <div className="pb-1 border-b" style={{ borderColor: 'var(--ds-border)' }}>
                {SUPPORTED_LANGUAGES.map(({ code, label }) => (
                  <button
                    key={code}
                    type="button"
                    role="menuitem"
                    onClick={() => { i18n.changeLanguage(code); setLangOpen(false); close(); }}
                    className={`ds-workspace-nav-item flex w-full items-center gap-2.5 pl-10 pr-4 py-2 text-left ${i18n.language === code ? 't-strong' : ''}`}
                  >
                    {i18n.language === code && <span className="absolute left-4 t-brand">✓</span>}
                    {label}
                  </button>
                ))}
              </div>
            )}

            {/* App Tour */}
            <button type="button" role="menuitem"
              onClick={() => { close(); window.dispatchEvent(new CustomEvent(FORCE_PRODUCT_TOUR_EVENT)); }}
              className="ds-workspace-nav-item flex w-full items-center gap-2.5 px-4 py-2.5 text-left">
              <Compass className="h-4 w-4 shrink-0" aria-hidden />
              <span className="flex-1">{t('layout.profile.appTour', 'App Tour')}</span>
            </button>

            <button type="button" role="menuitem"
              onClick={() => { close(); onNavChange('account-settings'); }}
              className="ds-workspace-nav-item flex w-full items-center gap-2.5 px-4 py-2.5 text-left">
              <Settings className="h-4 w-4 shrink-0" aria-hidden />
              <span className="flex-1">{t('layout.profile.manageAccount', 'Manage account')}</span>
            </button>

            <div className="my-1 mx-3 border-b" style={{ borderColor: 'var(--ds-border)' }} />

            {/* Sign out */}
            <button type="button" role="menuitem" onClick={() => { close(); onSignOutClick(); }}
              className="ds-workspace-nav-item is-danger flex w-full items-center gap-2.5 px-4 py-2.5 text-left">
              <LogOut className="h-4 w-4 shrink-0" aria-hidden />
              <span className="flex-1">{t('layout.profile.signOut', 'Sign out')}</span>
            </button>
          </div>

          <div
            className="flex items-center justify-between gap-2 border-t px-4 py-2.5"
            style={{ borderColor: 'var(--ds-border)', background: 'color-mix(in srgb, var(--ds-border) 25%, transparent)' }}
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <GGJLogoMark size={16} />
              <span className="t-meta t-muted truncate">GoGrabJob</span>
            </div>
            <span className="t-meta t-muted tabular-nums shrink-0">v{APP_VERSION}</span>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Public: UtilityCluster
// ─────────────────────────────────────────────────────────────────────────────
const UtilityCluster: React.FC<UtilityClusterProps> = ({
  userName,
  userEmail,
  userAvatarUrl,
  credits,
  creditsLoading,
  onUpgradeClick,
  onSignOutClick,
  onNavChange,
}) => {
  return (
    <>
      <span data-tour="credits-display">
        <CreditsPill
          credits={credits}
          creditsLoading={creditsLoading}
          onUpgradeClick={onUpgradeClick}
        />
      </span>

      <div className="mx-0.5 h-4 w-px shrink-0 bg-slate-100 dark:bg-slate-700" aria-hidden />

      {/* Blind mode toggle (B2C-specific) */}
      <BlindModeToggle />

      {/* Notifications */}
      <NotificationTrayButton onNavChange={onNavChange} />

      {/* Avatar pill with name — opens settings dropdown */}
      <AvatarMenu
        userName={userName}
        userEmail={userEmail}
        userAvatarUrl={userAvatarUrl}
        onSignOutClick={onSignOutClick}
        onNavChange={onNavChange}
        credits={credits}
        creditsLoading={creditsLoading}
        onUpgradeClick={onUpgradeClick}
      />
    </>
  );
};

export default UtilityCluster;
